import { useState, useEffect, useRef } from 'react';
import { API_URL } from '../config';

/**
 * Hook to poll order status until paid/unlocked or timeout.
 * @param {string} orderId 
 * @param {Object} options 
 * @param {number} [options.intervalMs=3000]
 * @param {number} [options.timeoutMs=60000]
 * @returns {{ status: string, unlocked: boolean, error: string|null, isPolling: boolean, stop: Function }}
 */
const useOrderStatusPoll = (orderId, { intervalMs = 3000, timeoutMs = 60000 } = {}) => {
  const [status, setStatus] = useState('pending');
  const [unlocked, setUnlocked] = useState(false);
  const [error, setError] = useState(null);
  const [isPolling, setIsPolling] = useState(false);
  
  const timeoutRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const startTimeRef = useRef(null);

  const stop = () => {
    setIsPolling(false);
    if (pollIntervalRef.current) clearTimeout(pollIntervalRef.current);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  };

  useEffect(() => {
    if (!orderId) {
      setStatus('pending');
      setError(null);
      setUnlocked(false);
      setIsPolling(false);
      return;
    }

    const checkStatus = async () => {
      try {
        const res = await fetch(`${API_URL}/api/pedidos/${orderId}/status`, {
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
        });
        
        if (!res.ok) throw new Error('Error verificando estado del pedido');
        
        const data = await res.json();
        
        // Update state
        setStatus(data.status);
        setUnlocked(data.unlocked);

        // Success condition: paid AND unlocked
        if (data.status === 'paid' && data.unlocked === true) {
            stop();
            return;
        }

        // Failure condition
        if (data.status === 'failed') {
            setError('El pago ha fallado o ha sido cancelado.');
            stop();
            return;
        }
        
        // Continue polling if not timed out
        if (Date.now() - startTimeRef.current < timeoutMs) {
            pollIntervalRef.current = setTimeout(checkStatus, intervalMs);
        } else {
            setError('Tiempo de espera agotado. Por favor verifica tu correo.');
            stop();
        }

      } catch (err) {
        console.warn('Polling error:', err);
        // Continue polling on network error? Or stop? 
        // Usually continue until timeout, but maybe with backoff. 
        // For simplicity, we continue.
        if (Date.now() - startTimeRef.current < timeoutMs) {
            pollIntervalRef.current = setTimeout(checkStatus, intervalMs);
        } else {
            setError('Tiempo de espera agotado. ' + err.message);
            stop();
        }
      }
    };

    const start = () => {
        setIsPolling(true);
        setError(null);
        startTimeRef.current = Date.now();
        checkStatus();
    };

    start();

    return () => stop();
  }, [orderId, intervalMs, timeoutMs]);

  return { status, unlocked, error, isPolling, stop };
};

export default useOrderStatusPoll;
